export type UserRole = 'doctor' | 'patient' | 'nushu-x';

export interface User {
  id: string;
  username: string;
  role: UserRole;
  email?: string;
  firstName?: string;
  lastName?: string;
}

export interface DoctorProfileName {
  first_name: string;
  middle_name: string;
  last_name: string;
}

export interface DoctorProfileAddress {
  street?: string;
  zip?: number;
  town?: string;
  country?: string;
}

export interface DoctorProfile {
  email: string;
  region: string;
  language: string;
  name: DoctorProfileName;
  affiliation: string;
  phone?: string;
  address: DoctorProfileAddress;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface LoginCredentials {
  userId: string;
  password: string;
  appId?: string;
}

export interface LoginResponseUser {
  id: string;
  username: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  role: string;
}

export interface LoginResponse {
  result: string;
  access_token: string;
  token_type: string;
  pryv_endpoint?: string;
  refresh?: string;
  user?: LoginResponseUser;
}

export function getUserRole(username: string): UserRole {
  if (username.startsWith('d-')) return 'doctor';
  if (username.startsWith('p-')) return 'patient';
  if (username.startsWith('x-')) return 'nushu-x';
  // Default to doctor for other username formats (e.g., emails)
  return 'doctor';
}

export function getSelfServiceBasePath(username: string): '/patient' | '/nushu-x' {
  return username.startsWith('x-') ? '/nushu-x' : '/patient';
}

/**
 * Validates that a role value is valid
 */
export function isValidUserRole(role: string | undefined | null): role is UserRole {
  return role === 'patient' || role === 'doctor' || role === 'nushu-x';
}

function normalizeApiRole(role: string): UserRole | null {
  const normalized = role.toLowerCase().trim();
  if (normalized === 'doctor') return 'doctor';
  if (normalized === 'patient') return 'patient';
  if (normalized === 'nushu-x' || normalized === 'nushux' || normalized === 'nushu_x') {
    return 'nushu-x';
  }
  return null;
}

/**
 * Validates that username role matches API role
 * Returns error message if mismatch, null if valid
 */
export function validateUserRole(
  username: string,
  apiRole: string | undefined | null
): string | null {
  // Determine role from username
  const usernameRole = getUserRole(username);

  // Check if username has a valid role prefix
  const hasValidPrefix =
    username.startsWith('p-') ||
    username.startsWith('d-') ||
    username.startsWith('x-');
  if (!hasValidPrefix) {
    return `Username "${username}" does not have a valid role prefix (p-, d-, or x-)`;
  }

  // Validate API role matches username role
  if (apiRole) {
    const normalizedApiRole = normalizeApiRole(apiRole);
    if (!normalizedApiRole) {
      return `Invalid API role: "${apiRole}". Must be "patient", "doctor", or "nushu-x"`;
    }

    // Backward compatibility: x- users may still be returned as "patient" by auth APIs.
    const isCompatibleXPatientRole =
      usernameRole === 'nushu-x' && normalizedApiRole === 'patient';

    if (normalizedApiRole !== usernameRole && !isCompatibleXPatientRole) {
      return `Role mismatch: username "${username}" indicates "${usernameRole}" but API returned "${normalizedApiRole}"`;
    }
  }

  return null; // Valid
}

export function isValidUsername(username: string): boolean {
  return username.trim().length > 0;
}

export function getUserDisplayName(user: User | null | undefined): string {
  if (!user) return 'User';

  const firstName = user.firstName?.trim();
  const lastName = user.lastName?.trim();

  if (firstName && lastName) return `${firstName} ${lastName}`;
  if (firstName) return firstName;
  if (lastName) return lastName;

  return user.username.replace(/^[dpx]-/, '').trim() || 'User';
}
